import { createClient } from '@supabase/supabase-js';
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// PostgREST caps each response at ~1000 rows regardless of any .limit() you pass,
// so a single .select() silently truncates large tables. fetchAllRows pages with
// .range() until a short page comes back, returning the full result set.
//
// IMPORTANT: range/offset paging is only stable when the query's ORDER BY is on a
// UNIQUE column. Paginating over a non-unique key (e.g. created_at, where a whole
// CSV batch shares one timestamp) lets Postgres return rows in a different order
// per request, so page boundaries overlap — the SAME row comes back on two pages
// and lands in the result twice. In the Lead Pool that produced duplicate rows
// with identical React keys, which then refused to update/disappear on filter or
// search and broke row selection. Always give paginated queries a unique tie-
// breaker (.order('id')); as a backstop we also drop any duplicate id here so a
// stray collision can never reach the UI again.
export async function fetchAllRows(buildQuery, { pageSize = 1000, parallel = 4 } = {}) {
  const all = [];
  const seenIds = new Set();
  const add = (data) => {
    for (const row of data) {
      // Only de-dupe rows that actually carry an id. Some callers select a
      // subset of columns (e.g. name, owner_id) with no id — those must all be
      // kept, so leave them untouched.
      if (row && row.id != null) {
        if (seenIds.has(row.id)) continue;
        seenIds.add(row.id);
      }
      all.push(row);
    }
  };
  const page = (n) => buildQuery().range(n * pageSize, n * pageSize + pageSize - 1);

  // First page on its own: most tables fit in one page.
  const first = await page(0);
  if (first.error) return { data: null, error: first.error };
  add(first.data || []);
  if (!first.data || first.data.length < pageSize) return { data: all, error: null };

  // Bigger tables: fetch the following pages a few at a time instead of one
  // after another, stopping at the first short or empty page. Pages are added
  // in order, so the result is the same as reading them one by one.
  for (let next = 1; ; next += parallel) {
    const batch = await Promise.all(Array.from({ length: parallel }, (_, i) => page(next + i)));
    let done = false;
    for (const r of batch) {
      if (r.error) return { data: null, error: r.error };
      const rows = r.data || [];
      add(rows);
      if (rows.length < pageSize) { done = true; break; }
    }
    if (done) break;
  }
  return { data: all, error: null };
}

// Supabase/PostgREST encodes .in('id', ids) as a query string. With UUIDs (~36
// chars each) ~150+ ids overflow the URL length limit and the request fails
// (often silently from the user's perspective). Bulk select-all merge / delete
// from the Lead Pool hit this exact wall. Chunk the id list and run one request
// per chunk, returning the first error encountered.
export async function runInChunks(ids, chunkSize, run) {
  if (!ids || ids.length === 0) return { error: null };
  for (let i = 0; i < ids.length; i += chunkSize) {
    const slice = ids.slice(i, i + chunkSize);
    const { error } = await run(slice);
    if (error) return { error };
  }
  return { error: null };
}
